import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  id: "results",
  class: "results-section"
}
const _hoisted_2 = { class: "shell" }
const _hoisted_3 = { class: "application-selector" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "comparison-stage" }
const _hoisted_6 = { class: "case-header" }
const _hoisted_7 = { class: "matrix-scroll" }
const _hoisted_8 = { class: "column-title" }
const _hoisted_9 = { class: "cell-title" }
const _hoisted_10 = ["src", "alt"]
const _hoisted_11 = {
  key: 1,
  class: "not-available"
}
const _hoisted_12 = ["src"]
const _hoisted_13 = { class: "case-footer" }
const _hoisted_14 = ["aria-label"]
const _hoisted_15 = { class: "case-gallery" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { class: "case-thumb" }
const _hoisted_18 = ["src", "alt"]

import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import rawCases from '../data/resultsCases.json'

type ResultCell = {
  id: string
  label: string
  kind: 'image' | 'video' | 'na'
  src: string
  featured?: boolean
}

type ResultCase = {
  id: string
  name: string
  category: string
  parameter: string
  description: string
  thumb: string
  contain?: boolean
  columns: Array<{ title: string; cells: ResultCell[] }>
  videoCount: number
}

type RawCell = { id: string; label: string; kind: 'image' | 'video' | 'na'; src?: string; video?: string }
type RawCase = {
  id: string
  name: string
  category: string
  description?: string
  thumb: string
  freeAspect?: boolean
  matrixColumns: Array<{ title: string; cells: RawCell[] }>
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ResultsExplorer',
  setup(__props) {

const parameterByCategory: Record<string, string> = {
  Pose: 'Rigid transform',
  Geometry: 'Mesh vertices',
  Skeleton: 'Joint rotations',
  Lighting: 'Light position',
  Material: 'Material parameters',
}
const descriptionByCategory: Record<string, string> = {
  Pose: 'Rigid pose recovery from a challenging distant initialization.',
  Geometry: 'Geometry reconstruction with image-space differentiable rendering.',
  Skeleton: 'Articulated pose recovery across many coupled joint parameters.',
  Lighting: 'Inverse lighting from shading and shadow observations.',
  Material: 'Differentiable recovery of spatially varying material parameters.',
}

const cases: ResultCase[] = (rawCases as RawCase[]).map(item => {
  const category = item.category === 'Lights' ? 'Lighting' : item.category
  const columns = item.matrixColumns.map(column => ({
    title: column.title,
    cells: column.cells.map(cell => ({
      id: cell.id,
      label: cell.label,
      kind: cell.kind,
      src: cell.src || cell.video || '',
      featured: /ours/i.test(cell.label),
    })),
  }))
  return {
    id: item.id,
    name: item.id === 'house' ? 'Fantasy house' : item.name,
    category,
    parameter: parameterByCategory[category],
    description: item.description || descriptionByCategory[category],
    thumb: item.thumb,
    contain: Boolean(item.freeAspect),
    columns,
    videoCount: columns.flatMap(column => column.cells).filter(cell => cell.kind === 'video').length,
  }
})

const applicationOrder = ['Pose', 'Geometry', 'Skeleton', 'Lighting', 'Material']
const applications = applicationOrder.map(name => ({ name, count: cases.filter(item => item.category === name).length }))

const activeCaseId = ref(cases[0].id)
const activeCase = computed(() => cases.find(item => item.id === activeCaseId.value) || cases[0])
const casePickerOpen = ref(false)
const pickerCategory = ref('Pose')
const visibleCases = computed(() => cases.filter(item => item.category === pickerCategory.value))
let videoRefs: HTMLVideoElement[] = []
let cleanupSync = (): void => {}

function resultMedia(path: string): string { return `${process.env.BASE_URL}results/${path}` }

function setVideoRef(element: unknown): void {
  if (element instanceof HTMLVideoElement && !videoRefs.includes(element)) videoRefs.push(element)
}

function startSync(): void {
  cleanupSync()
  const videos = videoRefs.filter(video => video.isConnected)
  if (!videos.length) return
  const master = videos[0]
  let animationFrame = 0

  const syncFollowers = (): void => {
    for (const video of videos.slice(1)) {
      if (Math.abs(video.currentTime - master.currentTime) > 0.08) video.currentTime = master.currentTime
      if (video.paused && !master.paused) video.play().catch(() => undefined)
      if (!video.paused && master.paused) video.pause()
    }
  }
  const tick = (): void => {
    syncFollowers()
    animationFrame = requestAnimationFrame(tick)
  }
  const onSeeked = (): void => syncFollowers()
  videos.forEach(video => video.addEventListener('seeked', onSeeked))
  Promise.all(videos.map(video => video.play().catch(() => undefined))).then(() => {
    syncFollowers()
    animationFrame = requestAnimationFrame(tick)
  })
  cleanupSync = () => {
    if (animationFrame) cancelAnimationFrame(animationFrame)
    videos.forEach(video => video.removeEventListener('seeked', onSeeked))
  }
}

async function selectCase(id: string): Promise<void> {
  if (id === activeCaseId.value) return
  cleanupSync()
  videoRefs = []
  activeCaseId.value = id
  await nextTick()
  startSync()
}

function openCasePicker(category: string): void {
  pickerCategory.value = category
  casePickerOpen.value = true
}

function closeCasePicker(): void { casePickerOpen.value = false }

async function openCase(id: string): Promise<void> {
  closeCasePicker()
  await selectCase(id)
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') closeCasePicker()
}

onMounted(async () => {
  window.addEventListener('keydown', onKeydown)
  await nextTick()
  startSync()
})
onBeforeUnmount(() => {
  cleanupSync()
  window.removeEventListener('keydown', onKeydown)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[4] || (_cache[4] = _createElementVNode("div", { class: "results-heading" }, [
        _createElementVNode("div", null, [
          _createElementVNode("p", { class: "kicker" }, "Selected results"),
          _createElementVNode("h2", null, [
            _createTextVNode("One method,"),
            _createElementVNode("br"),
            _createTextVNode("many applications.")
          ])
        ]),
        _createElementVNode("p", null, " Browse representative inverse-rendering tasks across pose, geometry, articulation, lighting, and material. Videos within each case are synchronized so convergence behavior can be compared frame by frame. ")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _cache[1] || (_cache[1] = _createElementVNode("span", null, "Choose application", -1)),
        _createElementVNode("div", null, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(applications), (application) => {
            return (_openBlock(), _createElementBlock("button", {
              key: application.name,
              type: "button",
              class: _normalizeClass({ active: activeCase.value.category === application.name }),
              onClick: ($event: any) => (openCasePicker(application.name))
            }, [
              _createTextVNode(_toDisplayString(application.name) + " ", 1),
              _createElementVNode("small", null, _toDisplayString(application.count), 1)
            ], 10, _hoisted_4))
          }), 128))
        ]),
        _createElementVNode("p", null, [
          _createElementVNode("b", null, _toDisplayString(activeCase.value.name), 1),
          _cache[0] || (_cache[0] = _createElementVNode("small", null, "Current case", -1))
        ])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("header", _hoisted_6, [
          _createElementVNode("div", null, [
            _createElementVNode("p", null, _toDisplayString(activeCase.value.category) + " · " + _toDisplayString(activeCase.value.parameter), 1),
            _createElementVNode("h3", null, _toDisplayString(activeCase.value.name), 1)
          ]),
          _createElementVNode("p", null, _toDisplayString(activeCase.value.description), 1),
          _cache[2] || (_cache[2] = _createElementVNode("span", { class: "sync-badge" }, [
            _createElementVNode("i"),
            _createTextVNode("Synchronized playback")
          ], -1))
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", {
            class: "matrix-grid",
            style: _normalizeStyle({ '--column-count': activeCase.value.columns.length })
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeCase.value.columns, (column, columnIndex) => {
              return (_openBlock(), _createElementBlock("section", {
                key: `${activeCase.value.id}-${column.title}-${columnIndex}`,
                class: "matrix-column"
              }, [
                _createElementVNode("p", _hoisted_8, [
                  _createElementVNode("span", null, "0" + _toDisplayString(columnIndex + 1), 1),
                  _createTextVNode(_toDisplayString(column.title), 1)
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(column.cells, (cell) => {
                  return (_openBlock(), _createElementBlock("article", {
                    key: `${activeCase.value.id}-${cell.id}`,
                    class: _normalizeClass(["matrix-cell", { featured: cell.featured }])
                  }, [
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("b", null, _toDisplayString(cell.label), 1),
                      _createElementVNode("span", null, _toDisplayString(cell.kind === 'image' ? 'Still' : cell.kind === 'video' ? 'Video' : 'N/A'), 1)
                    ]),
                    _createElementVNode("div", {
                      class: _normalizeClass(["matrix-frame", { contain: activeCase.value.contain }])
                    }, [
                      (cell.kind === 'image')
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            src: resultMedia(cell.src),
                            alt: `${activeCase.value.name} ${cell.label}`,
                            loading: "lazy"
                          }, null, 8, _hoisted_10))
                        : (cell.kind === 'na')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_11, "Not available"))
                          : (_openBlock(), _createElementBlock("video", {
                              key: 2,
                              ref_for: true,
                              ref: setVideoRef,
                              autoplay: "",
                              muted: "",
                              loop: "",
                              playsinline: "",
                              preload: "metadata",
                              controls: ""
                            }, [
                              _createElementVNode("source", {
                                src: resultMedia(cell.src),
                                type: "video/mp4"
                              }, null, 8, _hoisted_12)
                            ], 512))
                    ], 2)
                  ], 2))
                }), 128))
              ]))
            }), 128))
          ], 4)
        ]),
        _createElementVNode("footer", _hoisted_13, [
          _cache[3] || (_cache[3] = _createElementVNode("p", null, [
            _createElementVNode("span", null, "Reading the matrix"),
            _createTextVNode(" Initialization and target appear first; baselines and our generated-reference optimization follow.")
          ], -1)),
          _createElementVNode("b", null, _toDisplayString(activeCase.value.videoCount) + " synchronized videos", 1)
        ])
      ])
    ]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (casePickerOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "case-picker-backdrop",
            onClick: _withModifiers(closeCasePicker, ["self"])
          }, [
            _createElementVNode("section", {
              class: "case-picker",
              role: "dialog",
              "aria-modal": "true",
              "aria-label": `${pickerCategory.value} result cases`
            }, [
              _createElementVNode("header", null, [
                _createElementVNode("div", null, [
                  _cache[5] || (_cache[5] = _createElementVNode("p", null, "Application gallery", -1)),
                  _createElementVNode("h3", null, _toDisplayString(pickerCategory.value) + " cases", 1)
                ]),
                _createElementVNode("span", null, _toDisplayString(visibleCases.value.length) + " cases", 1),
                _createElementVNode("button", {
                  type: "button",
                  "aria-label": "Close case picker",
                  onClick: closeCasePicker
                }, "×")
              ]),
              _createElementVNode("div", _hoisted_15, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleCases.value, (item, index) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: item.id,
                    type: "button",
                    class: _normalizeClass({ selected: item.id === activeCase.value.id }),
                    onClick: ($event: any) => (openCase(item.id))
                  }, [
                    _createElementVNode("div", _hoisted_17, [
                      _createElementVNode("img", {
                        src: resultMedia(item.thumb),
                        alt: `${item.name} preview`,
                        loading: "lazy"
                      }, null, 8, _hoisted_18),
                      _createElementVNode("span", null, "0" + _toDisplayString(index + 1), 1)
                    ]),
                    _createElementVNode("p", null, [
                      _createElementVNode("small", null, _toDisplayString(item.parameter), 1),
                      _createElementVNode("b", null, _toDisplayString(item.name), 1)
                    ]),
                    _cache[6] || (_cache[6] = _createElementVNode("i", null, "Open case →", -1))
                  ], 10, _hoisted_16))
                }), 128))
              ])
            ], 8, _hoisted_14)
          ]))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})